<p-toast position="bottom-center"></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>

    <app-page-hero
        eyebrow="Fleet Mobilization"
        title="Planning & Road Tracking"
        subtitle="Plan daily destination demand by truck type, compare mobilized capacity, and follow linked shipment deliveries.">
        <div nav>
            <p-button label="Overview" icon="pi pi-chart-line" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization"></p-button>
            <p-button label="Entries" icon="pi pi-list" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/entries"></p-button>
            <p-button label="Planning" icon="pi pi-route" routerLink="/fleet/mobilization/planning"></p-button>
            <p-button label="Approvals" icon="pi pi-check-square" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/change-requests"></p-button>
            <p-button label="History" icon="pi pi-history" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/history"></p-button>
        </div>
        <div actions>
            <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" (onClick)="loadPlanning()"></p-button>
            <p-button label="New Mobilization" icon="pi pi-plus" routerLink="/fleet/mobilization/new"></p-button>
        </div>
    </app-page-hero>

    <app-page-search-panel
        title="Planning Filters"
        [activeFilters]="activeFilterCount()"
        [resultText]="resultText()"
        [collapsed]="true"
        (search)="loadPlanning()"
        (clear)="clearFilters()">
        <div class="formgrid grid planning-filter-grid">
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-datePicker inputId="planning-date-range" [(ngModel)]="filters.dateRange" selectionMode="range" [readonlyInput]="true" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
                        <label for="planning-date-range">Planning Date</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>

            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-truck"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="planning-truck-type" [options]="vehicleTypes" [(ngModel)]="filters.vehicleTypeId" optionLabel="name" optionValue="id" [showClear]="true" [filter]="true" filterBy="name" placeholder="Any truck type" appendTo="body" styleClass="w-full"></p-select>
                        <label for="planning-truck-type">Truck Type</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>

            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="planning-destination-location" appLazyLocationPicker [(ngModel)]="filters.destinationLocationId" optionLabel="name" optionValue="id" [showClear]="true" [filter]="true" filterBy="name,shortDescription,longDescription,externalLocationReference" placeholder="Any location" appendTo="body" styleClass="w-full">
                            <ng-template pTemplate="item" let-location>
                                <div class="option-stack">
                                    <span>{{ locationLabel(location) }}</span>
                                    <small>{{ location.shortDescription || location.longDescription || '-' }}</small>
                                </div>
                            </ng-template>
                            <ng-template pTemplate="selectedItem" let-location>
                                <span>{{ location ? locationLabel(location) : 'Any location' }}</span>
                            </ng-template>
                        </p-select>
                        <label for="planning-destination-location">Destination Location</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>

            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input pInputText id="planning-destination" type="text" [(ngModel)]="filters.destination" placeholder="Destination text">
                        <label for="planning-destination">Destination</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>

            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-compass"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="planning-job-status" [options]="jobStatusOptions" [(ngModel)]="filters.jobStatus" optionLabel="label" optionValue="value" [showClear]="true" placeholder="Any tracking status" appendTo="body" styleClass="w-full"></p-select>
                        <label for="planning-job-status">Road Tracking Status</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>

            <div class="field col-12 md:col-4 planning-toggle-field">
                <div class="toggle-row">
                    <p-checkbox inputId="include-open-releases" [(ngModel)]="filters.includeOpenReleases" [binary]="true"></p-checkbox>
                    <label for="include-open-releases">Include open releases</label>
                </div>
                <div class="toggle-row">
                    <p-checkbox inputId="include-tracking" [(ngModel)]="filters.includeTracking" [binary]="true"></p-checkbox>
                    <label for="include-tracking">Include road tracking</label>
                </div>
            </div>
        </div>
    </app-page-search-panel>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
        <p-tablist>
            <p-tab [value]="0"><span class="tab-label"><i class="pi pi-users"></i> Availability</span></p-tab>
            <p-tab [value]="1"><span class="tab-label"><i class="pi pi-calendar-clock"></i> Daily Planning</span></p-tab>
            <p-tab [value]="2"><span class="tab-label"><i class="pi pi-truck"></i> Truck Type Dashboard</span></p-tab>
            <p-tab [value]="3"><span class="tab-label"><i class="pi pi-route"></i> Road Tracking</span></p-tab>
        </p-tablist>

        <p-tabpanels>
            <p-tabpanel [value]="1">
                <app-page-data-panel title="Daily Destination Planning" subtitle="Demand and mobilized capacity grouped by planning date, destination, and truck type." [resultText]="planningRows.length + ' rows'">
                    <p-table [value]="planningRows" [loading]="isLoading" dataKey="date" styleClass="p-datatable-sm p-datatable-gridlines" [tableStyle]="{ 'min-width': '88rem' }" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]" responsiveLayout="scroll">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Date</th>
                                <th>Destination</th>
                                <th>Truck Type</th>
                                <th>Demand</th>
                                <th>Open Releases</th>
                                <th>Shipments</th>
                                <th>Assigned Deliveries</th>
                                <th>Mobilized Capacity</th>
                                <th>Gap</th>
                                <th>Active</th>
                                <th>In Transit</th>
                                <th>Delivered</th>
                                <th>Delayed</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td>{{ dateLabel(row.date) }}</td>
                                <td>
                                    <div class="stacked-cell">
                                        <span>{{ destinationLabel(row) }}</span>
                                        <small *ngIf="row.destinationLocationId">Location ID {{ row.destinationLocationId }}</small>
                                    </div>
                                </td>
                                <td>{{ vehicleTypeLabel(row) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.demandTruckCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.openReleaseCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.shipmentCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.assignedDeliveryCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.mobilizedCapacityCount) }}</td>
                                <td><p-tag [value]="numberLabel(row.capacityGap).toString()" [severity]="capacitySeverity(row)"></p-tag></td>
                                <td class="numeric-cell">{{ numberLabel(row.activeCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.inTransitCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.deliveredCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.delayedCount) }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="13" class="empty-cell">No planning demand for the selected filters.</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>
            </p-tabpanel>

            <p-tabpanel [value]="2">
                <app-page-data-panel title="Daily Truck-Type Dashboard" subtitle="Mobilization capacity grouped by day and truck type from the dashboard API." [resultText]="vehicleTypeRows.length + ' rows'">
                    <p-table [value]="vehicleTypeRows" [loading]="isLoading" dataKey="date" styleClass="p-datatable-sm p-datatable-gridlines" [tableStyle]="{ 'min-width': '64rem' }" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]" responsiveLayout="scroll">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Date</th>
                                <th>Truck Type</th>
                                <th>Mobilizations</th>
                                <th>Active</th>
                                <th>Assigned Vehicles</th>
                                <th>Assigned Trailers</th>
                                <th>Salary</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td>{{ dateLabel(row.date) }}</td>
                                <td>{{ vehicleTypeLabel(row) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.mobilizationCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.activeMobilizationCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.assignedVehicleCount) }}</td>
                                <td class="numeric-cell">{{ numberLabel(row.assignedTrailerCount) }}</td>
                                <td>{{ amountLabel(row.salaryTotalAmount) }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="7" class="empty-cell">No truck-type dashboard rows for the selected filters.</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>
            </p-tabpanel>

            <p-tabpanel [value]="3">
                <app-page-data-panel title="Road Tracking" subtitle="Shipment deliveries linked back to mobilizations and the existing per-delivery ETA endpoint." [resultText]="roadRows.length + ' deliveries'">
                    <p-table [value]="roadRows" [loading]="isLoading" dataKey="deliveryId" styleClass="p-datatable-sm p-datatable-gridlines" [tableStyle]="{ 'min-width': '92rem' }" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]" responsiveLayout="scroll">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Date</th>
                                <th>Delivery</th>
                                <th>Shipment</th>
                                <th>Mobilization</th>
                                <th>Driver / Vehicle</th>
                                <th>Destination</th>
                                <th>Truck Type</th>
                                <th>Status</th>
                                <th>Schedule</th>
                                <th>Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td>{{ dateLabel(row.planningDate) }}</td>
                                <td>
                                    <button type="button" class="link-button" (click)="openDelivery(row)">
                                        {{ row.deliveryNumber || ('DEL-' + row.deliveryId) }}
                                    </button>
                                    <small class="muted-line">ID {{ row.deliveryId || '-' }}</small>
                                </td>
                                <td>
                                    <div class="stacked-cell">
                                        <span>{{ row.shipmentNumber || ('SHP-' + row.shipmentId) }}</span>
                                        <small>Shipment ID {{ row.shipmentId || '-' }}</small>
                                    </div>
                                </td>
                                <td>
                                    <button *ngIf="row.mobilizationId; else noMobilization" type="button" class="link-button" (click)="openMobilization(row)">
                                        {{ row.mobilizationNumber || ('MOB-' + row.mobilizationId) }}
                                    </button>
                                    <ng-template #noMobilization><span>-</span></ng-template>
                                </td>
                                <td>
                                    <div class="stacked-cell">
                                        <span>{{ row.driverName || (row.driverId ? 'Driver name unavailable' : '-') }}</span>
                                        <small>{{ row.vehiclePlateNumber || (row.vehicleId ? 'Vehicle plate unavailable' : 'Vehicle not assigned') }}</small>
                                    </div>
                                </td>
                                <td>{{ destinationLabel(row) }}</td>
                                <td>{{ vehicleTypeLabel(row) }}</td>
                                <td>
                                    <div class="stacked-cell">
                                        <p-tag [value]="row.trackingState || 'UNKNOWN'" [severity]="trackingSeverity(row.trackingState)"></p-tag>
                                        <small>{{ jobStatusLabel(row.jobStatus) }}</small>
                                    </div>
                                </td>
                                <td>
                                    <div class="stacked-cell">
                                        <span>Ship {{ dateTimeLabel(row.scheduledShippingDate) }}</span>
                                        <small>Pickup {{ dateTimeLabel(row.pickupTime) }}</small>
                                        <small>Dropoff {{ dateTimeLabel(row.dropoffTime) }}</small>
                                    </div>
                                </td>
                                <td>
                                    <div class="table-actions">
                                        <p-button icon="pi pi-eye" [rounded]="true" [text]="true" severity="help" size="small" (onClick)="openDelivery(row)" ariaLabel="Open delivery"></p-button>
                                        <p-button icon="pi pi-clock" [rounded]="true" [text]="true" severity="secondary" size="small" [disabled]="!row.etaUrl" (onClick)="openEta(row)" ariaLabel="Open ETA"></p-button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="10" class="empty-cell">No road tracking rows for the selected filters.</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>
            </p-tabpanel>

            <p-tabpanel [value]="0">
                <div class="availability-grid">
                    <app-page-data-panel title="Driver Availability" subtitle="Free vs busy company-linked drivers for the selected planning window.">
                        <div class="chart-shell">
                            <p-chart type="doughnut" [data]="driverStatusChartData" [options]="availabilityChartOptions"></p-chart>
                        </div>
                    </app-page-data-panel>

                    <app-page-data-panel title="Truck Availability" subtitle="Free vs busy company-linked trucks, including maintenance blocked units.">
                        <div class="chart-shell">
                            <p-chart type="doughnut" [data]="vehicleStatusChartData" [options]="availabilityChartOptions"></p-chart>
                        </div>
                    </app-page-data-panel>

                    <app-page-data-panel title="Utilization" [subtitle]="utilizationHelpText()">
                        <div class="chart-shell">
                            <p-chart type="bar" [data]="utilizationChartData" [options]="utilizationChartOptions"></p-chart>
                        </div>
                    </app-page-data-panel>
                </div>

                <app-page-data-panel title="FIFO Allocation Recommendations" subtitle="Advisory driver and truck pairings only. No assignments are changed." [resultText]="fifoPairs.length + ' pairs'">
                    <div class="fifo-grid" *ngIf="fifoPairs.length; else noFifoPairs">
                        <article class="fifo-card" *ngFor="let pair of fifoPairs">
                            <div class="fifo-rank">#{{ pair.rank }}</div>
                            <div class="fifo-card-body">
                                <strong>{{ pair.driverName || 'Driver name unavailable' }}</strong>
                                <span>{{ pair.vehicleLabel || 'Vehicle plate unavailable' }}</span>
                                <small>{{ pair.vehicleTypeName || 'Any truck type' }} | Available {{ pair.availableAt ? dateTimeLabel(pair.availableAt) : 'now' }}</small>
                                <small>{{ pair.reason }}</small>
                            </div>
                            <div class="table-actions">
                                <p-button icon="pi pi-user" [rounded]="true" [text]="true" size="small" severity="secondary" (onClick)="openDriver(pair)" ariaLabel="Open driver"></p-button>
                                <p-button icon="pi pi-truck" [rounded]="true" [text]="true" size="small" severity="secondary" (onClick)="openVehicle(pair)" ariaLabel="Open vehicle"></p-button>
                            </div>
                        </article>
                    </div>
                    <ng-template #noFifoPairs>
                        <div class="empty-cell">No FIFO pair can be recommended for the selected filters.</div>
                    </ng-template>
                </app-page-data-panel>

                <app-page-data-panel title="Driver Availability" subtitle="Current work, latest status, next free time, and utilization." [resultText]="driverAvailabilityRows.length + ' drivers'">
                    <p-table [value]="driverAvailabilityRows" [loading]="isLoading" dataKey="resourceId" styleClass="p-datatable-sm p-datatable-gridlines" [tableStyle]="{ 'min-width': '88rem' }" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]" responsiveLayout="scroll">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Driver</th>
                                <th>Status</th>
                                <th>Current Work</th>
                                <th>Latest Status</th>
                                <th>Available At</th>
                                <th>Next Free</th>
                                <th>Utilization</th>
                                <th>FIFO</th>
                                <th>Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td><div class="stacked-cell"><span>{{ row.displayName }}</span><small>{{ row.secondaryLabel }} | {{ row.ownershipType }}</small></div></td>
                                <td><p-tag [value]="availabilityStatusLabel(row.availabilityStatus)" [severity]="availabilitySeverity(row.availabilityStatus)" [styleClass]="availabilityStatusClass(row.availabilityStatus)"></p-tag></td>
                                <td>{{ availabilityWorkLabel(row) }}</td>
                                <td>{{ row.latestStatus || '-' }}</td>
                                <td>{{ availableAtLabel(row) }}</td>
                                <td>{{ nextFreeLabel(row) }}</td>
                                <td><div class="stacked-cell"><span>{{ utilizationLabel(row.utilizationPercent) }}</span><p-progressBar [value]="row.utilizationPercent || 0" [showValue]="false"></p-progressBar></div></td>
                                <td>{{ row.fifoRank ? '#' + row.fifoRank : '-' }}</td>
                                <td>
                                    <div class="table-actions">
                                        <p-button icon="pi pi-eye" [rounded]="true" [text]="true" size="small" severity="help" [disabled]="!row.currentWork" (onClick)="openAvailabilityWork(row)" ariaLabel="Open work"></p-button>
                                        <p-button icon="pi pi-box" [rounded]="true" [text]="true" size="small" severity="secondary" [disabled]="!row.currentWork?.shipmentId" (onClick)="openShipmentFromWork(row)" ariaLabel="Open shipment"></p-button>
                                        <p-button icon="pi pi-user" [rounded]="true" [text]="true" size="small" severity="secondary" (onClick)="openDriver(row)" ariaLabel="Open driver"></p-button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="9" class="empty-cell">No drivers match the selected availability filters.</td></tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>

                <app-page-data-panel title="Truck Availability" subtitle="Current work, latest status, next free time, maintenance state, and utilization." [resultText]="vehicleAvailabilityRows.length + ' trucks'">
                    <p-table [value]="vehicleAvailabilityRows" [loading]="isLoading" dataKey="resourceId" styleClass="p-datatable-sm p-datatable-gridlines" [tableStyle]="{ 'min-width': '94rem' }" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]" responsiveLayout="scroll">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Truck</th>
                                <th>Status</th>
                                <th>Current Work</th>
                                <th>Latest Status</th>
                                <th>Available At</th>
                                <th>Next Free</th>
                                <th>Maintenance</th>
                                <th>Utilization</th>
                                <th>FIFO</th>
                                <th>Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td><div class="stacked-cell"><span>{{ row.displayName }}</span><small>{{ row.vehicleTypeName || row.secondaryLabel }} | {{ row.ownershipType }}</small></div></td>
                                <td><p-tag [value]="availabilityStatusLabel(row.availabilityStatus)" [severity]="availabilitySeverity(row.availabilityStatus)" [styleClass]="availabilityStatusClass(row.availabilityStatus)"></p-tag></td>
                                <td>{{ availabilityWorkLabel(row) }}</td>
                                <td>{{ row.latestStatus || '-' }}</td>
                                <td>{{ availableAtLabel(row) }}</td>
                                <td>{{ nextFreeLabel(row) }}</td>
                                <td>{{ row.maintenanceStatus || 'Available' }}</td>
                                <td><div class="stacked-cell"><span>{{ utilizationLabel(row.utilizationPercent) }}</span><p-progressBar [value]="row.utilizationPercent || 0" [showValue]="false"></p-progressBar></div></td>
                                <td>{{ row.fifoRank ? '#' + row.fifoRank : '-' }}</td>
                                <td>
                                    <div class="table-actions">
                                        <p-button icon="pi pi-eye" [rounded]="true" [text]="true" size="small" severity="help" [disabled]="!row.currentWork" (onClick)="openAvailabilityWork(row)" ariaLabel="Open work"></p-button>
                                        <p-button icon="pi pi-box" [rounded]="true" [text]="true" size="small" severity="secondary" [disabled]="!row.currentWork?.shipmentId" (onClick)="openShipmentFromWork(row)" ariaLabel="Open shipment"></p-button>
                                        <p-button icon="pi pi-truck" [rounded]="true" [text]="true" size="small" severity="secondary" (onClick)="openVehicle(row)" ariaLabel="Open vehicle"></p-button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="10" class="empty-cell">No trucks match the selected availability filters.</td></tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
</app-page-shell>
